import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import QRCode from 'qrcode';
import { Copy, QrCode, X, Download, Calendar, Clock, User, Sparkles } from 'lucide-react';
import AppointmentsTab from '../components/dashboard/AppointmentsTab';
import AvailabilitiesTab from '../components/dashboard/AvailabilitiesTab';
import ProfileTab from '../components/dashboard/ProfileTab';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('appointments');
  const clientUrl = import.meta.env.VITE_CLIENT_URL || 'http://192.168.1.6:5173';
  const VITE_SERVER_URL = import.meta.env.VITE_SERVER_URL || 'https://easyrdv.onrender.com';

  // État pour le QR code
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, availabilitiesRes] = await Promise.all([
        axios.get(`${VITE_SERVER_URL}/api/appointments`),
        axios.get(`${VITE_SERVER_URL}/api/availability`)
      ]);

      // Vérifier la structure de la réponse et gérer les différents cas
      const appointments = appointmentsRes.data?.data?.appointments || appointmentsRes.data?.appointments || [];
      const availabilities = availabilitiesRes.data?.data?.availabilities || availabilitiesRes.data?.availabilities || [];

      setAppointments(appointments);
      setAvailabilities(availabilities);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      // En cas d'erreur, initialiser avec des tableaux vides
      setAppointments([]);
      setAvailabilities([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour générer le QR code
  const generateQRCode = async () => {
    const bookingUrl = `${clientUrl}/${user?.username}`;
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(bookingUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeData(qrCodeDataUrl);
      setShowQRCode(true);
    } catch (error) {
      console.error('Erreur lors de la génération du QR code:', error);
      alert('Erreur lors de la génération du QR code');
    }
  };

  // Fonction pour copier le lien
  const copyLink = async () => {
    const bookingUrl = `${clientUrl}/${user?.username}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(bookingUrl);
        notification.success({
          message: 'Succès',
          description: 'Lien copié dans le presse-papiers !',
          placement: 'top',
          style: {
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(80, 36, 255, 0.12)',
            background: 'linear-gradient(90deg, #6366f1 0%, #a21caf 100%)',
            color: '#fff',
            fontWeight: 500,
          },
          duration: 2.5,
        });
      } catch (error) {
        notification.error({
          message: 'Erreur',
          description: 'Erreur lors de la copie du lien.',
          placement: 'top',
          style: {
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(255, 36, 36, 0.12)',
            background: 'linear-gradient(90deg, #ef4444 0%, #be185d 100%)',
            color: '#fff',
            fontWeight: 500,
          },
          duration: 2.5,
        });
        console.error('Erreur lors de la copie:', error);
      }
    } else {
      // Fallback for older browsers
      try {
        const textarea = document.createElement('textarea');
        textarea.value = bookingUrl;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        notification.success({
          message: 'Succès',
          description: 'Lien copié dans le presse-papiers !',
          placement: 'topRight',
          style: {
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(80, 36, 255, 0.12)',
            background: 'linear-gradient(90deg, #6366f1 0%, #a21caf 100%)',
            color: '#fff',
            fontWeight: 500,
          },
          duration: 2.5,
        });
      } catch (error) {
        notification.error({
          message: 'Erreur',
          description: 'Erreur lors de la copie du lien (fallback).',
          placement: 'top',
          style: {
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(255, 36, 36, 0.12)',
            background: 'linear-gradient(90deg, #ef4444 0%, #be185d 100%)',
            color: '#fff',
            fontWeight: 500,
          },
          duration: 2.5,
        });
        console.error('Erreur lors de la copie (fallback):', error);
      }
    }
  };

  // Fonction pour télécharger le QR code
  const downloadQRCode = () => {
    if (qrCodeData) {
      const link = document.createElement('a');
      link.download = `qr-code-${user?.username}.png`;
      link.href = qrCodeData;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 lg:space-y-10 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-white to-indigo-50 rounded-2xl shadow-sm border border-indigo-100 p-6 sm:p-8">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {t('dashboard.title')}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {t('dashboard.welcome')}, <span className="font-semibold text-indigo-600">{user?.name}</span> ! ({user?.profession})
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.stats.appointments')}</p>
                <p className="text-2xl font-bold text-gray-900">{appointments.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.stats.availabilities')}</p>
                <p className="text-2xl font-bold text-gray-900">{availabilities.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('common.status')}</p>
                <p className="text-lg font-semibold text-green-600">{t('common.active')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Lien public */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
              <Copy className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{t('profile.publicLink.title')}</h3>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            <input
              type="text"
              value={`${clientUrl}/${user?.username}`}
              readOnly
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <div className="flex space-x-2">
              <button
                onClick={copyLink}
                className="flex-1 sm:flex-none px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-sm font-medium hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
              >
                <Copy className="w-4 h-4" />
                <span>{t('common.copy')}</span>
              </button>
              <button
                onClick={generateQRCode}
                className="flex-1 sm:flex-none px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-sm font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
              >
                <QrCode className="w-4 h-4" />
                <span>{t('profile.publicLink.qrCode')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-indigo-50">
          <nav className="-mb-px flex flex-wrap sm:flex-nowrap space-x-1 sm:space-x-8 px-4 sm:px-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('appointments')}
              className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-300 ${activeTab === 'appointments'
                  ? 'border-indigo-500 text-indigo-600 bg-white rounded-t-lg'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{t('navigation.appointments')} ({appointments.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('availabilities')}
              className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-300 ${activeTab === 'availabilities'
                  ? 'border-indigo-500 text-indigo-600 bg-white rounded-t-lg'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>{t('navigation.availabilities')} ({availabilities.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-300 ${activeTab === 'profile'
                  ? 'border-indigo-500 text-indigo-600 bg-white rounded-t-lg'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>{t('navigation.profile')}</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6 sm:p-8">
          {activeTab === 'appointments' ? (
            <AppointmentsTab />
          ) : activeTab === 'availabilities' ? (
            <AvailabilitiesTab />
          ) : (
            <ProfileTab />
          )}
        </div>
      </div>

      {/* Modal QR Code */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm sm:max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">{t('profile.publicLink.qrCode')} {t('booking.title')}</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-2xl shadow-lg border border-gray-200">
                  <img
                    src={qrCodeData}
                    alt="QR Code de réservation"
                    className="max-w-full h-auto"
                  />
                </div>
              </div>

              <p className="text-sm text-gray-600">
                {t('profile.publicLink.description')}
              </p>

              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 justify-center">
                <button
                  onClick={downloadQRCode}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-sm font-medium hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
                >
                  <Download className="w-4 h-4" />
                  <span>{t('common.download')}</span>
                </button>
                <button
                  onClick={() => setShowQRCode(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-300 transition-all duration-300"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 